/**
 * Fits the results stage into the centre column: the video (with the map beside it when there is one) on top,
 * the timeline strip under both. The page is fixed-height, so the video is as wide as the height allows and the
 * slack goes to the strip, up to a share of the height; a tall column gives the video the whole width instead.
 */

// the strip's share of the stage height: never smaller than the first, never larger than the second
export const STRIP_MIN_SHARE = 0.16;
export const STRIP_MAX_SHARE = 0.3;
// the map's share of the width when it sits beside the video
export const MAP_SHARE = 0.36;

/**
 * @param width stage width (px)
 * @param height stage height (px)
 * @param ratio video width / height
 * @param gap gap between the rows and the columns (px)
 * @param chrome height of the player's control bar below the picture (px)
 * @param side the map sits beside the video
 * @param stripFloor the least height the strip's panel can take (px), whatever the share says
 * @param stripShares [least, most] share of the height for the strip (a bigger video: a thin strip)
 * @returns { videoWidth, rowHeight, stripHeight } or null while the stage has no size yet
 */
export const fitStage = ({
	width,
	height,
	ratio,
	gap,
	chrome,
	side,
	stripFloor = 0,
	stripShares = [STRIP_MIN_SHARE, STRIP_MAX_SHARE],
}) => {
	if (!(width > 0) || !(height > 0) || !(ratio > 0)) return null;
	const stripMin = Math.max(stripFloor, Math.ceil(height * stripShares[0]));
	const stripMax = Math.max(stripMin, Math.floor(height * stripShares[1]));
	// whole pixels, rounded so the rows never add up to more than the stage
	const pictureMax = Math.max(0, Math.floor(height) - gap - stripMin - chrome - 1);
	const widthMax = side ? (width - gap) * (1 - MAP_SHARE) : width;
	const videoWidth = Math.floor(Math.max(0, Math.min(widthMax, pictureMax * ratio)));
	const rowHeight = Math.ceil(videoWidth / ratio + chrome);
	const stripHeight = Math.min(
		stripMax,
		Math.max(stripMin, Math.floor(height) - gap - rowHeight)
	);
	return { videoWidth, rowHeight, stripHeight };
};
